import { useEffect } from "react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { GhostLink, AssessmentButton } from "@/components/Ctas";
import { CALENDLY_URL } from "@/config";

const CHALLENGES = [
  "Too many priorities and not enough clarity",
  "An idea that hasn't yet become a strong product or service",
  "A business that depends too heavily on the founder",
  "Processes that have grown organically and are becoming difficult to manage",
  "Difficulty deciding what to build, improve or stop",
  "Knowing AI could help, but not knowing where to start",
  "Growth creating more complexity instead of more capacity",
];

const AREAS = [
  {
    number: "01",
    title: "Find the Focus",
    description:
      "Clarify the opportunity, customer, product/service direction and priorities.",
    services: ["Product & service strategy", "Opportunity definition", "Prioritization", "Roadmapping", "Product thinking", "Business model thinking"],
    testid: "founders-area-focus",
  },
  {
    number: "02",
    title: "Build the System",
    description:
      "Create the structure and ways of working needed to execute without everything depending on the founder.",
    services: ["Operating processes", "Workflow design", "Program/project management", "Team ways of working", "Delegation", "Continuous improvement", "Fractional Product Management"],
    testid: "founders-area-system",
  },
  {
    number: "03",
    title: "Add Intelligence",
    description:
      "Identify where AI and automation can genuinely improve the business.",
    services: ["AI opportunity mapping", "AI readiness", "Workflow automation", "AI-powered operations", "Practical AI implementation"],
    testid: "founders-area-intelligence",
  },
];

const METHOD = ["ASSESS", "CLARIFY", "PRIORITIZE", "BUILD", "LEARN", "ADAPT"];

const ENGAGEMENTS = [
  {
    name: "Strategy Sprint",
    description:
      "For founders who need clarity around an opportunity, product, service or strategic direction.",
    testid: "engagement-strategy-sprint",
  },
  {
    name: "Product / Business Advisory",
    description:
      "Ongoing support to prioritize, make decisions and move important work forward.",
    testid: "engagement-advisory",
  },
  {
    name: "Fractional Product Management",
    description:
      "Hands-on product leadership and execution support when a full-time product leader isn't yet needed.",
    testid: "engagement-fractional-pm",
  },
  {
    name: "Systems & AI",
    description:
      "For founders ready to improve processes, reduce operational friction and identify practical opportunities for AI and automation.",
    testid: "engagement-systems-ai",
  },
];

export default function Founders() {
  useEffect(() => {
    document.title = "For Founders — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="founders-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="founders-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              For Founders
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              Build the business behind the idea.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              You have the vision. KP Strategy helps you turn it into a clearer
              strategy, stronger product or service, and a more practical way
              of working.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <AssessmentButton testid="founders-cta-assessment" dark={false} />
              <GhostLink testid="founders-cta-book-call" href={CALENDLY_URL}>
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="founders-challenges">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — The Reality"
            title="You have the ideas. The challenge is knowing what to do next."
            testid="founders-challenges-head"
          />
          <ul className="mt-12 grid max-w-4xl grid-cols-1 gap-x-12 sm:grid-cols-2" data-testid="founders-challenges-list">
            {CHALLENGES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <li className="flex items-baseline gap-3 border-b border-line py-4 text-base leading-relaxed text-body">
                  <span className="text-amber" aria-hidden="true">✦</span>
                  {c}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* How KP Strategy helps */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="founders-areas">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="02 — How I Help"
            title="Product thinking. Systems thinking. Practical execution."
            testid="founders-areas-head"
          />
          <div className="mt-14 border-t border-ink/10">
            {AREAS.map((a, i) => (
              <Reveal key={a.number} delay={i * 0.08}>
                <div
                  data-testid={a.testid}
                  className="grid grid-cols-1 gap-6 border-b border-ink/10 py-10 lg:grid-cols-12 lg:gap-10"
                >
                  <div className="lg:col-span-4">
                    <p className="font-serif text-5xl font-light text-amber/90">
                      {a.number}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl font-normal text-ink sm:text-3xl">
                      {a.title}
                    </h3>
                    <p className="mt-3 max-w-sm text-base leading-relaxed text-body">
                      {a.description}
                    </p>
                  </div>
                  <ul className="flex flex-wrap content-start gap-2 lg:col-span-8 lg:pt-2">
                    {a.services.map((s) => (
                      <li
                        key={s}
                        className="border border-line bg-paper px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider text-body transition-colors duration-300 hover:border-amber/60 hover:text-ink"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="founders-methodology">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="03 — The Approach"
            title="One methodology. Adapted to where you are today."
            copy="Every engagement starts from the same foundation — then flexes to your stage, your team and your constraints."
            testid="founders-methodology-head"
          />
          <Reveal delay={0.15}>
            <ol className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0" data-testid="founders-methodology-strip">
              {METHOD.map((step, i) => (
                <li key={step} className="flex items-center">
                  <span className="flex items-baseline gap-2">
                    <span className="font-mono text-[11px] text-amber">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-ink">
                      {step}
                    </span>
                  </span>
                  {i < METHOD.length - 1 && (
                    <span className="hidden px-4 text-ink/30 sm:inline" aria-hidden="true">
                      →
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Engagement formats */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="founders-engagements">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="04 — Working Together"
            title="What working together can look like"
            copy="Flexible formats, shaped around the problem — not rigid packages."
            testid="founders-engagements-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.08} className="h-full">
                <div
                  data-testid={e.testid}
                  className="group h-full border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <h3 className="font-serif text-2xl font-normal text-ink">
                    {e.name}
                  </h3>
                  <span className="mt-5 block h-px w-10 bg-amber transition-all duration-500 group-hover:w-full" />
                  <p className="mt-5 text-base leading-relaxed text-body">
                    {e.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="founders-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              05 — Next Step
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
              Not sure where to start?
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
              Get your Free Strategy &amp; Systems Assessment. Identify where
              you&apos;re losing clarity, capacity or momentum — and what to
              focus on next.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-7">
              <AssessmentButton testid="founders-final-cta-assessment" dark />
              <GhostLink
                testid="founders-final-cta-book-call"
                href={CALENDLY_URL}
                dark
              >
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
